import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { DASHBOARD_STUDENT_ADD_NOTE, DASHBOARD_UPDATE_STUDENT_NOTE } from '../queries';
import { Modal, Input, Button, message } from 'antd';
import SpinLoading from 'common/components/spin-loading';

const { TextArea } = Input;

const NoteModal = props => {
  const {
    value = '',
    noteId,
    studentId,
    landlordId,
    maxLength = 500,
    isOpen,
    successMessage,
    onClose,
    onAdded,
    onUpdated,
  } = props;
  const { t } = useTranslation();
  const [note, setNote] = useState(value);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState(noteId);

  const [createStudentNote, { loading: createLoading }] = useMutation(DASHBOARD_STUDENT_ADD_NOTE, {
    onError: e => {
      message.error(e?.message?.toString());
    },
    onCompleted: () => {
      onAdded?.();
      setNote('');
      message.success(
        <div className="add-note-modal__success">
          {successMessage ?? t('cms:cms.students.list.modal.note_success')}
        </div>,
      );
    },
  });

  const [updateStudentNote, { loading: updateLoading }] = useMutation(
    DASHBOARD_UPDATE_STUDENT_NOTE,
    {
      onError: e => {
        message.error(e?.message?.toString());
      },
      onCompleted: () => {
        onUpdated?.();
        setNote('');
        message.success(t('cms:cms.students.list.modal.note_update_success'));
      },
    },
  );

  const handleChange = e => {
    const { value } = e.target;
    setNote(value);
  };

  const handleConfirm = async () => {
    if (!note) {
      message.error(t('cms:cms.students.list.modal.note_empty'));
      return;
    }

    if (note === value) {
      handleCancel();
      return;
    }

    if (noteId) {
      try {
        updateStudentNote({
          variables: {
            input: {
              id,
              note: note,
            },
          },
        });
      } catch (error) {
        message.error(error?.toString());
      }
    } else {
      try {
        await createStudentNote({
          variables: {
            input: {
              studentId,
              landlordId,
              note,
            },
          },
        });
      } catch (e) {
        message.error(e?.toString());
      }
    }
  };

  const handleCancel = () => {
    setNote(value);
    onClose?.();
  };

  useEffect(() => {
    setNote(value);
  }, [value]);

  useEffect(() => {
    setLoading(createLoading || updateLoading);
  }, [createLoading, updateLoading]);

  useEffect(() => {
    setId(noteId);
  }, [noteId]);

  return (
    <Modal
      className="add-note-modal"
      title={
        <div className="add-note-modal__title">
          {value?.length
            ? t('cms:cms.students.list.modal.edit_note')
            : t('cms:cms.students.list.modal.add_note')}
        </div>
      }
      visible={isOpen}
      onCancel={handleCancel}
      footer={
        <div className="add-note-modal__footer">
          {loading && <SpinLoading className="add-note-modal__loading" />}
          <Button
            size="large"
            type="secondary"
            className="add-note-modal__cancel"
            onClick={handleCancel}
          >
            {t('cms:cms.students.list.modal.cancel')}
          </Button>
          <Button
            size="large"
            type="primary"
            className="add-note-modal__confirm"
            onClick={handleConfirm}
            disabled={loading || note.length === 0}
          >
            {t('cms:cms.students.list.modal.confirm')}
          </Button>
        </div>
      }
      width={450}
    >
      <div className="add-note-modal-content">
        <div className="add-note-modal__label">{t('cms:cms.students.list.modal.note')}</div>
        <TextArea
          className="add-note-modal__textarea"
          rows={4}
          value={note}
          placeholder={t('cms:cms.students.list.modal.add_note_placeholder')}
          maxLength={maxLength}
          autoSize={{ minRows: 4, maxRows: 6 }}
          onChange={handleChange}
        />
        <div className="add-note-modal_textarea-count">{`${note?.length ?? 0} / ${maxLength}`}</div>
      </div>
    </Modal>
  );
};

export default NoteModal;
